import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import {
    Container,
    Box,
    Typography,
    Tabs,
    Tab,
    Paper,
    Alert,
    CircularProgress,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import {
    SportsEsports as GameIcon,
    EmojiEvents as ChallengeIcon,
    People as LFGIcon,
    Event as SessionIcon,
    Leaderboard as RankingIcon,
    VideogameAsset as GamesIcon,
    Dashboard as OverviewIcon,
    CalendarToday as EventIcon,
    DirectionsCar as ACCIcon,
    Timer as TimerIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import gulagService from '../services/gulag';
import timeTrialService from '../services/timeTrials';
import { getGameImage } from '../utils/gameImages';

// Import critical components (always needed)
import CompactChallengeCard from '../components/gulag/CompactChallengeCard';
import MatchResultDialog from '../components/gulag/MatchResultDialog';
import GamePreferenceSelector from '../components/gulag/GamePreferenceSelector';
import SessionsHero from '../components/gulag/SessionsHero';
import TimeTrialDetailsModal from '../components/gulag/TimeTrialDetailsModal';

// Lazy load heavy tab components for better initial load performance
const ChallengeList = lazy(() => import('../components/gulag/ChallengeList'));
const LFGBoard = lazy(() => import('../components/gulag/LFGBoard'));
const SessionList = lazy(() => import('../components/gulag/SessionList'));
const Leaderboard = lazy(() => import('../components/gulag/Leaderboard'));
const GameList = lazy(() => import('../components/gulag/GameList'));
const ACCLeaderboard = lazy(() => import('../components/acc/ACCLeaderboard'));
const TrackmaniaLeaderboard = lazy(() => import('../components/trackmania/TrackmaniaLeaderboard'));

function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`gulag-tabpanel-${index}`}
            aria-labelledby={`gulag-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

function Gulag() {
    const { eventId: routeEventId } = useParams();
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Event selection
    const [events, setEvents] = useState([]);
    const [selectedEventId, setSelectedEventId] = useState(routeEventId || '');
    
    // Data states
    const [myStats, setMyStats] = useState(null);
    const [incomingChallenges, setIncomingChallenges] = useState([]);
    // const [outgoingChallenges, setOutgoingChallenges] = useState([]); // Not currently used
    const [acceptedChallenges, setAcceptedChallenges] = useState([]);
    const [nextSessionData, setNextSessionData] = useState(null);
    
    // Rankings state (shared across components)
    const [rankings, setRankings] = useState([]);
    const [rankingsLoading, setRankingsLoading] = useState(false);
    
    // Time trial states
    const [timeTrials, setTimeTrials] = useState([]);
    const [loadingTrials, setLoadingTrials] = useState(false);
    
    // Dialog states
    const [resultDialogOpen, setResultDialogOpen] = useState(false);
    const [selectedChallenge, setSelectedChallenge] = useState(null);
    const [success, setSuccess] = useState(null);
    const [timeTrialModalOpen, setTimeTrialModalOpen] = useState(false);
    const [selectedTimeTrial, setSelectedTimeTrial] = useState(null);
    
    // Ref to track if initial load is complete
    const initialLoadComplete = useRef(false);
    
    // Ref to track previous active tab for refresh logic
    const previousTab = useRef(activeTab);

    // Initialize on mount - load events first
    useEffect(() => {
        loadEvents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle route event ID changes
    useEffect(() => {
        if (routeEventId) {
            setSelectedEventId(routeEventId);
        }
    }, [routeEventId]);

    // Load data when event changes (but not on first mount if no event)
    useEffect(() => {
        if (initialLoadComplete.current && selectedEventId) {
            // Only reload if this isn't the initial load
            loadInitialData();
            loadActiveTimeTrials();
        } else if (selectedEventId && !initialLoadComplete.current) {
            // First time we have an event selected, load everything
            loadInitialData();
            loadActiveTimeTrials();
            initialLoadComplete.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEventId]);
    
    // Refresh data when switching back to Overview tab
    useEffect(() => {
        // Only refresh if:
        // 1. Switching TO Overview tab (activeTab === 0)
        // 2. Coming FROM a different tab (previousTab.current !== 0)
        // 3. Initial load is complete (not first render)
        if (activeTab === 0 && previousTab.current !== 0 && initialLoadComplete.current) {
            console.log('Refreshing Overview tab data after switching from another tab');
            loadInitialData();
        }
        
        // Update previous tab reference
        previousTab.current = activeTab;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const loadEvents = async () => {
        const result = await gulagService.getGulagEvents();
        if (result.success) {
            setEvents(result.data);
            // If no event selected and events available, select the first one
            if (!selectedEventId && result.data.length > 0) {
                setSelectedEventId(result.data[0].id);
            }
        }
    };

    const loadRankings = async () => {
        if (!selectedEventId) {
            setRankings([]);
            return;
        }
        
        setRankingsLoading(true);
        try {
            const result = await gulagService.getEventRankings(selectedEventId, null, 100);
            if (result.success && result.data) {
                setRankings(result.data);
            } else {
                setRankings([]);
            }
        } catch (err) {
            console.error('Error loading rankings:', err);
            setRankings([]);
        } finally {
            setRankingsLoading(false);
        }
    };

    const loadInitialData = async () => {
        setLoading(true);
        setError(null);

        try {
            // Load all data in parallel for better performance
            const [
                statsResult,
                incomingResult,
                acceptedIncomingResult,
                acceptedOutgoingResult
            ] = await Promise.all([
                gulagService.getMyStats(),
                gulagService.getIncomingChallenges('pending'),
                gulagService.getIncomingChallenges('accepted'),
                gulagService.getOutgoingChallenges('accepted')
            ]);

            // Set user stats
            if (statsResult.success) {
                setMyStats(statsResult.data);
            }

            // Set incoming challenges
            if (incomingResult.success) {
                setIncomingChallenges(incomingResult.data);
            }

            // Combine accepted challenges
            const allAccepted = [];
            if (acceptedIncomingResult.success) {
                allAccepted.push(...acceptedIncomingResult.data);
            }
            if (acceptedOutgoingResult.success) {
                allAccepted.push(...acceptedOutgoingResult.data);
            }
            setAcceptedChallenges(allAccepted);
            
            // Match counts are not currently displayed
            // Load match counts for accepted challenges using batch endpoint
            // if (selectedEventId && allAccepted.length > 0) {
            //     const batchRequests = allAccepted
            //         .map((challenge) => {
            //             const opponentId = challenge.challenger?.id || challenge.challenged?.id;
            //             const gameId = challenge.game_id;
            //             if (opponentId && gameId) {
            //                 return {
            //                     event_id: selectedEventId,
            //                     game_id: gameId,
            //                     opponent_id: opponentId
            //                 };
            //             }
            //             return null;
            //         })
            //         .filter(req => req !== null);
            //     
            //     if (batchRequests.length > 0) {
            //         const batchResult = await gulagService.getBatchMatchCounts(batchRequests);
            //         if (batchResult.success) {
            //             // setMatchCounts(batchResult.data);
            //         }
            //     }
            // }

            // Load rankings and session data in parallel
            if (selectedEventId) {
                const [sessionResult] = await Promise.all([
                    gulagService.getNextSession(selectedEventId),
                    loadRankings() // Load rankings here to share across components
                ]);
                
                if (sessionResult.success && sessionResult.data) {
                    setNextSessionData(sessionResult.data);
                } else {
                    setNextSessionData(null);
                }
            } else {
                setNextSessionData(null);
                setRankings([]);
            }
        } catch (err) {
            console.error('Error loading Gulag data:', err);
            setError('Failed to load Gulag data');
        } finally {
            setLoading(false);
        }
    };

    const loadActiveTimeTrials = async () => {
        setLoadingTrials(true);
        try {
            const result = await timeTrialService.getTimeTrials(
                selectedEventId, 
                'active'
            );
            if (result.success) {
                setTimeTrials(result.data);
            } else {
                setTimeTrials([]);
            }
        } catch (err) {
            console.error('Error loading time trials:', err);
            setTimeTrials([]);
        } finally {
            setLoadingTrials(false);
        }
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleChallengeUpdate = () => {
        // Reload challenges and rankings after an action
        loadInitialData();
    };

    const handleAcceptChallenge = async (challengeId) => {
        const result = await gulagService.acceptChallenge(challengeId);
        if (result.success) {
            setSuccess('Challenge accepted! It now appears in Open Challenges.');
            // Auto-dismiss success message after 3 seconds
            setTimeout(() => setSuccess(null), 3000);
            await loadInitialData();
        } else {
            const errorMsg = typeof result.error === 'string' 
                ? result.error 
                : 'Failed to accept challenge';
            setError(errorMsg);
        }
    };

    const handleDeclineChallenge = async (challengeId) => {
        const result = await gulagService.declineChallenge(challengeId);
        if (result.success) {
            setSuccess('Challenge declined');
            // Auto-dismiss success message after 3 seconds
            setTimeout(() => setSuccess(null), 3000);
            await loadInitialData();
        } else {
            const errorMsg = typeof result.error === 'string' 
                ? result.error 
                : 'Failed to decline challenge';
            setError(errorMsg);
        }
    };

    const handleReportResult = (challenge) => {
        setSelectedChallenge(challenge);
        setResultDialogOpen(true);
    };

    const handleResultSubmit = async (resultData) => {
        if (!selectedChallenge) return;

        const result = await gulagService.completeChallenge(selectedChallenge.id, resultData);
        if (result.success) {
            setSuccess('Match result reported successfully!');
            // Auto-dismiss success message after 3 seconds
            setTimeout(() => setSuccess(null), 3000);
            setResultDialogOpen(false);
            setSelectedChallenge(null);
            await loadInitialData();
        } else {
            const errorMsg = typeof result.error === 'string' 
                ? result.error 
                : 'Failed to report result';
            setError(errorMsg);
        }
    };

    const loadSessionData = async () => {
        if (selectedEventId) {
            const sessionResult = await gulagService.getNextSession(selectedEventId);
            console.log('📅 Session data loaded:', sessionResult.data);
            if (sessionResult.success && sessionResult.data) {
                setNextSessionData(sessionResult.data);
            } else {
                setNextSessionData(null);
            }
        }
    };

    const handleJoinSession = async (sessionId) => {
        console.log('🎮 Joining session:', sessionId);
        const result = await gulagService.joinSession(sessionId);
        console.log('🎮 Join result:', result);
        if (result.success) {
            setSuccess('Joined session successfully!');
            // Immediately reload session data to update button state
            await loadSessionData();
            loadInitialData(); // Also reload everything else
        } else {
            setError(result.error || 'Failed to join session');
        }
    };

    const handleLeaveSession = async (sessionId) => {
        const result = await gulagService.leaveSession(sessionId);
        if (result.success) {
            setSuccess('Left session successfully!');
            // Immediately reload session data to update button state
            await loadSessionData();
            loadInitialData(); // Also reload everything else
        } else {
            setError(result.error || 'Failed to leave session');
        }
    };

    const handleViewAllSessions = () => {
        setActiveTab(4); // Switch to Sessions tab (index 4)
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <GameIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                        <Box>
                            <Typography variant="h3" component="h1" sx={{ fontWeight: 700 }}>
                                The Gulag
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Challenge. Compete. Conquer. Earn your glory in casual and competitive matches!
                            </Typography>
                        </Box>
                    </Box>
                    
                    {/* Event Selector */}
                    <FormControl sx={{ minWidth: 250 }} size="small">
                        <InputLabel>Event</InputLabel>
                        <Select
                            value={selectedEventId}
                            onChange={(e) => setSelectedEventId(e.target.value)}
                            label="Event"
                            startAdornment={<EventIcon sx={{ mr: 1, color: 'action.active' }} />}
                        >
                            <MenuItem value="">
                                <em>No Event (Global Only)</em>
                            </MenuItem>
                            {events.map((event) => (
                                <MenuItem key={event.id} value={event.id}>
                                    {event.name}
                                    {event.status && (
                                        <Chip 
                                            label={event.status} 
                                            size="small" 
                                            sx={{ ml: 1 }}
                                            color={event.status === 'ongoing' ? 'success' : 'default'}
                                        />
                                    )}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {/* Overview Stats */}
            {myStats && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Total Points
                                </Typography>
                                <Typography variant="h4" color="primary">
                                    {myStats.global_ranking?.total_points?.toFixed(0) || 0}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Global Rank
                                </Typography>
                                <Typography variant="h4" color="secondary">
                                    #{myStats.global_ranking?.rank_position || 'N/A'}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Win Rate
                                </Typography>
                                <Typography variant="h4" color="success.main">
                                    {myStats.global_ranking?.win_rate?.toFixed(1) || 0}%
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Pending Challenges
                                </Typography>
                                <Typography variant="h4" color="warning.main">
                                    {incomingChallenges.length}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Tabs Navigation */}
            <Paper sx={{ mb: 3 }}>
                <Tabs 
                    value={activeTab} 
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab icon={<OverviewIcon />} label="Overview" iconPosition="start" />
                    <Tab icon={<ChallengeIcon />} label="Challenges" iconPosition="start" />
                    <Tab icon={<LFGIcon />} label="Looking For" iconPosition="start" />
                    <Tab icon={<SessionIcon />} label="Sessions" iconPosition="start" />
                    <Tab icon={<RankingIcon />} label="Rankings" iconPosition="start" />
                    <Tab icon={<GamesIcon />} label="Games" iconPosition="start" />
                    
                    {/* Dynamic Time Trial Tabs */}
                    {timeTrials.map((trial) => (
                        <Tab 
                            key={trial.id}
                            icon={<ACCIcon />} 
                            label={trial.name} 
                            iconPosition="start" 
                        />
                    ))}
                </Tabs>
            </Paper>

            {/* Sessions Hero - Only show when event is selected */}
            {selectedEventId && (
                <SessionsHero
                    sessionData={nextSessionData}
                    currentUserId={user?._id || user?.id}
                    onJoinSession={handleJoinSession}
                    onLeaveSession={handleLeaveSession}
                    onViewAllSessions={handleViewAllSessions}
                />
            )}

            {/* Tab Panels */}
            <TabPanel value={activeTab} index={0}>
                {/* Game Preferences Section */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GameIcon />
                        Select Games for Challenges
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Choose games where you want to receive and accept challenges from other players. Toggle availability on/off as needed.
                    </Typography>
                    <GamePreferenceSelector eventId={selectedEventId} />
                </Paper>

                {/* Challenges Section - Compact Side-by-Side Layout */}
                {(incomingChallenges.length > 0 || acceptedChallenges.length > 0) && (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {/* Incoming Challenges */}
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2, height: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <ChallengeIcon color="warning" />
                                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                                        Incoming ({incomingChallenges.length})
                                    </Typography>
                                </Box>
                                {incomingChallenges.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 3 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No incoming challenges
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {incomingChallenges.map((challenge) => (
                                            <CompactChallengeCard
                                                key={challenge.id}
                                                challenge={challenge}
                                                type="incoming"
                                                onAccept={() => handleAcceptChallenge(challenge.id)}
                                                onDecline={() => handleDeclineChallenge(challenge.id)}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </Paper>
                        </Grid>

                        {/* Open Challenges (Accepted) */}
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2, height: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <ChallengeIcon color="success" />
                                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                                        Open Challenges ({acceptedChallenges.length})
                                    </Typography>
                                </Box>
                                {acceptedChallenges.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 3 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No open challenges
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {acceptedChallenges.map((challenge) => (
                                            <CompactChallengeCard
                                                key={challenge.id}
                                                challenge={challenge}
                                                type="accepted"
                                                onReportResult={() => handleReportResult(challenge)}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                )}

                {/* Time Trials Widget */}
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TimerIcon />
                        Time Trials
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Active time trial events. Click to view details and rules.
                    </Typography>

                    {loadingTrials ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : timeTrials.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <TimerIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="body2" color="text.secondary">
                                No active time trials at the moment. Check back later!
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: 'repeat(2, 1fr)',
                                    sm: 'repeat(3, 1fr)',
                                    md: 'repeat(4, 1fr)',
                                    lg: 'repeat(6, 1fr)',
                                    xl: 'repeat(8, 1fr)'
                                },
                                gap: 2
                            }}
                        >
                            {timeTrials.map((trial) => {
                                const getStatusColor = (status) => {
                                    switch (status) {
                                        case 'upcoming': return 'info';
                                        case 'active': return 'success';
                                        case 'finalized': return 'default';
                                        case 'cancelled': return 'error';
                                        default: return 'default';
                                    }
                                };

                                // Create a game object for getGameImage
                                const gameForImage = {
                                    name: trial.game_name,
                                    background_image: trial.game_background_image
                                };

                                return (
                                    <Card
                                        key={trial.id}
                                        sx={{
                                            position: 'relative',
                                            borderRadius: 3,
                                            overflow: 'hidden',
                                            border: 1,
                                            borderColor: 'divider',
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                boxShadow: 6,
                                                zIndex: 10
                                            }
                                        }}
                                        onClick={() => {
                                            setSelectedTimeTrial(trial);
                                            setTimeTrialModalOpen(true);
                                        }}
                                    >
                                        {/* Game Image with Overlay */}
                                        <Box sx={{ position: 'relative', height: 140, overflow: 'hidden' }}>
                                            <CardMedia
                                                component="img"
                                                height="140"
                                                image={getGameImage(gameForImage)}
                                                alt={trial.name}
                                                sx={{
                                                    objectFit: 'cover',
                                                    transition: 'transform 0.3s ease'
                                                }}
                                            />
                                            
                                            {/* Gradient Overlay */}
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0.7) 100%)'
                                                }}
                                            />

                                            {/* Timer Icon Overlay */}
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 8,
                                                    right: 8,
                                                    bgcolor: 'rgba(0,0,0,0.6)',
                                                    borderRadius: '50%',
                                                    p: 0.5,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <TimerIcon sx={{ fontSize: 24, color: 'white' }} />
                                            </Box>
                                        </Box>

                                        <CardContent sx={{ p: 2 }}>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    fontWeight: 700,
                                                    mb: 1,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    minHeight: 40
                                                }}
                                            >
                                                {trial.name}
                                            </Typography>

                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <Chip
                                                    label={trial.status?.toUpperCase() || 'ACTIVE'}
                                                    size="small"
                                                    color={getStatusColor(trial.status)}
                                                    sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                                                />
                                                {trial.end_time && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                        Ends: {new Date(trial.end_time).toLocaleDateString()}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </Box>
                    )}
                </Paper>
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
                <Suspense fallback={
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                }>
                    <ChallengeList 
                        eventId={selectedEventId} 
                        onUpdate={handleChallengeUpdate}
                        rankings={rankings}
                        rankingsLoading={rankingsLoading}
                    />
                </Suspense>
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
                <Suspense fallback={
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                }>
                    <LFGBoard eventId={selectedEventId} />
                </Suspense>
            </TabPanel>

            <TabPanel value={activeTab} index={3}>
                <Suspense fallback={
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                }>
                    <SessionList eventId={selectedEventId} />
                </Suspense>
            </TabPanel>

            <TabPanel value={activeTab} index={4}>
                <Suspense fallback={
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                }>
                    <Leaderboard 
                        eventId={selectedEventId} 
                        rankings={rankings}
                        rankingsLoading={rankingsLoading}
                        onRefresh={loadRankings}
                    />
                </Suspense>
            </TabPanel>

            <TabPanel value={activeTab} index={5}>
                <Suspense fallback={
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                }>
                    <GameList />
                </Suspense>
            </TabPanel>

            {/* Dynamic Time Trial Panels - start at index 6 */}
            {timeTrials.map((trial, index) => {
                const tabIndex = 6 + index; // Start after static tabs
                const isACC = trial.game_name?.toLowerCase().includes('acc');
                const isTM = trial.game_name?.toLowerCase().includes('trackmania');
                
                return (
                    <TabPanel value={activeTab} index={tabIndex} key={trial.id}>
                        <Suspense fallback={
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        }>
                            {isACC && <ACCLeaderboard trialId={trial.id} trialName={trial.name} />}
                            {isTM && <TrackmaniaLeaderboard trialId={trial.id} trialName={trial.name} />}
                            {!isACC && !isTM && (
                                <Alert severity="info">
                                    Leaderboard not available for this game type.
                                </Alert>
                            )}
                        </Suspense>
                    </TabPanel>
                );
            })}

            {/* Match Result Dialog */}
            <MatchResultDialog
                open={resultDialogOpen}
                onClose={() => {
                    setResultDialogOpen(false);
                    setSelectedChallenge(null);
                }}
                challenge={selectedChallenge}
                onSubmit={handleResultSubmit}
            />

            {/* Time Trial Details Modal */}
            <TimeTrialDetailsModal
                open={timeTrialModalOpen}
                timeTrial={selectedTimeTrial}
                onClose={() => {
                    setTimeTrialModalOpen(false);
                    setSelectedTimeTrial(null);
                }}
            />
        </Container>
    );
}

export default Gulag;

